import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import * as userService from '../services/userService'
import * as translationService from '../services/translationService'
import * as networkService from '../services/networkService'
import { Hero, Button, DisplayText, TextContainer, ErrorText, PropertyBar, Option } from '../components'

import * as routeService from '../services/routeService'
import flowSelectors from '../selectors/flowSelector'
import {
  updateApplicationInformation,
  PHASE_INFORMATION_SUCCESS,
  softUpdateApplicationInformation,
} from '../actions/FlowAction'
import Profile from './Profile/Profile'
import { validateApplication } from './../utils/validation'
import { ERROR_ACCEPT_DATA_USAGE } from '../utils/validation/constants'

const getPhase1Items = zipCode =>
  routeService.isBrusselsZipCode(zipCode) ? ['rentIntention', 'income'] : ['residents', 'rentIntention', 'income']
const phase2Items = ['residents', 'contract', 'rentIntention', 'income', 'documents']

class OverviewView extends React.Component {
  static propTypes = {
    id: PropTypes.any,
    user: PropTypes.any,
    application: PropTypes.any,
    property: PropTypes.any,
    isRentIntentionValid: PropTypes.bool,
    residentsSummary: PropTypes.string,
    rentIntentionSummary: PropTypes.string,
    incomeSummary: PropTypes.string,
    documentsSummary: PropTypes.string,
    step: PropTypes.any,
    applicationConfirmationEmail: PropTypes.any,
    updateApplicationInformation: PropTypes.func,
    softUpdateApplicationInformation: PropTypes.func,
    updatePhaseInformation: PropTypes.func,
    isFirstPhase: PropTypes.bool,
    isIncomeRequired: PropTypes.bool,
    shouldIncludeIncomeStatement: PropTypes.bool,
  }

  state = {
    mainError: null,
    currentItem: null,
    isSceneModalOpen: false,
    confirmationEmail: true,
    applicationConfirmationEmail: true,
    isInformationModalOpen: false,
    isDeleteInformationModalOpen: false,
    email: '',
    pdfState: {},
    items: [],
    isSubmitting: false,
    isBrusselsZip: false,
  }

  modalRef = {}

  componentDidMount() {
    this.setState({
      applicationConfirmationEmail: this.props.applicationConfirmationEmail,
      email: this.props.user && this.props.user.email,
      touched: false,
      isBrusselsZip: routeService.isBrusselsZipCode(this.props.property.zip),
    })
  }

  handleVisitRequestClick = event => {
    event.preventDefault()
    const { application } = this.props
    if (!application.acceptDataUsages) {
      this.setState({
        mainError: translationService.t(ERROR_ACCEPT_DATA_USAGE),
        isSubmitting: false,
        touched: true,
      })
    } else {
      this.setState(
        {
          isSubmitting: true,
        },
        async () => {
          await networkService.updateApplicationProfile(application)
          await networkService
            .fetchApi(`/users/${this.props.id}/application/sendToBroker/${this.props.property.id}`, {}, 'post')
            .then(() => {
              // TODO: PHASE INFORMATION SHOULD COME FROM THE BACKEND ON SUCCESSFULL SEND !
              const now = new Date()
              this.props.updatePhaseInformation(now.toISOString(), this.props.step)
              routeService.goToThankyouPage()
            })
            .catch(() =>
              this.setState({
                mainError: translationService.t('system.500.server.error'),
                isSubmitting: false,
              }),
            )
        },
      )
    }
  }

  phasesOnGoing() {
    return userService.firstPhaseOnGoing() || userService.secondPhaseOnGoing()
  }

  onChange({ target: { name, value } }) {
    this.setState({
      [name]: value,
    })
  }

  handleAcceptDataUsages = ({ target: { checked } }) => {
    this.props.softUpdateApplicationInformation({ acceptDataUsages: checked })
  }

  render() {
    const { mainError, touched } = this.state
    const { application, property, isFirstPhase, isIncomeRequired, shouldIncludeIncomeStatement } = this.props
    const items = isFirstPhase ? getPhase1Items(property.zip) : phase2Items

    const allStepsValid = validateApplication(
      {
        ...application,
        isIncomeRequired,
        shouldIncludeIncomeStatement,
        isFirstPhase,
      },
      items,
      this.state.isBrusselsZip,
    )

    return (
      <div>
        {!isEmpty(property) ? (
          <React.Fragment>
            {property && <PropertyBar property={property} />}
            <Hero>
              <TextContainer>
                <DisplayText>
                  {application.contacts && (
                    <React.Fragment>
                      {allStepsValid
                        ? translationService.overviewTitle(application.contacts[0].firstname)
                        : translationService.overviewTitleInvalid(application.contacts[0].firstname)}
                    </React.Fragment>
                  )}
                </DisplayText>
                {mainError && <ErrorText>{mainError}</ErrorText>}
              </TextContainer>
            </Hero>
          </React.Fragment>
        ) : (
          <Hero>
            <DisplayText size="small">
              {application.contacts && translationService.overviewEditProfile(application.contacts[0].firstname)}
            </DisplayText>
          </Hero>
        )}
        <TextContainer>
          <Profile items={items} />
          <Option
            type="checkbox"
            label={translationService.overviewAcceptDataUsage()}
            id="acceptDataUsages"
            name="acceptDataUsages"
            checked={application.acceptDataUsages}
            onChange={this.handleAcceptDataUsages}
            hasError={touched && !application.acceptDataUsages}
          />
        </TextContainer>
        <Hero>
          <TextContainer>
            <Button
              size="large"
              primary
              loading={this.state.isSubmitting}
              onClick={this.handleVisitRequestClick}
              disabled={!allStepsValid}>
              {translationService.overviewResendAction()}
            </Button>
            <div>
              <Button plain url="/profiel/aanvragen">
                {translationService.overviewResendBackToProfile()}
              </Button>
            </div>
          </TextContainer>
        </Hero>
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  updateApplicationInformation: information => dispatch(updateApplicationInformation(information)),
  updatePhaseInformation: (completedAt, step) => {
    dispatch({
      type: PHASE_INFORMATION_SUCCESS,
      completedAt,
      step,
    })
  },
  softUpdateApplicationInformation: information => dispatch(softUpdateApplicationInformation(information)),
})
const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {}, error } = AuthReducer
  const { step } = FlowReducer
  const { mobileNumber, id } = user
  return {
    mobileNumber,
    id,
    user,
    error,
    application: flowSelectors.application(FlowReducer),
    property: flowSelectors.property(FlowReducer),
    residentsSummary: flowSelectors.residentsSummary(FlowReducer),
    contractSummary: flowSelectors.contractSummary(FlowReducer),
    rentIntentionSummary: flowSelectors.rentIntentionSummary(FlowReducer),
    incomeSummary: flowSelectors.incomeSummary(FlowReducer),
    documentsSummary: flowSelectors.documentsSummary(FlowReducer),
    step,
    applicationConfirmationEmail: FlowReducer.application.applicationConfirmationEmail,
    isFirstPhase: flowSelectors.getIsFirstPhase(FlowReducer),
    isIncomeRequired: flowSelectors.getIsIncomeRequired(FlowReducer),
    shouldIncludeIncomeStatement: flowSelectors.getShouldIncludeIncomeStatement(FlowReducer),
  }
}

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(OverviewView)



// WEBPACK FOOTER //
// ./src/views/ResendView.js